/**
 * KYS forms (ADR 0033): the quality-management forms of a tenant filled in and reported from FTS
 * instead of on paper.
 *
 *   * FR-12 "Günlük Forklift Kontrol" = a ready checklist template (library template, read-only; a tenant
 *     copies or assigns it). It carries the form code and revision, which every submission snapshots, and
 *     each submission gets a running document number per tenant and form ("KYS-FR-12-000005").
 *     Approval ("Onaylayan") is a role and a timestamp, never a person's name (owner decision).
 *   * FR-11 "Envanter Bakım Takip" = the vehicle list plus a maintenance plan (interval, responsible ROLE)
 *     and maintenance records (periodic maintenance, annual periodic inspection, repair).
 *
 * Only the form STRUCTURE is here (item wording, columns, code, revision). No person or customer names.
 * ADR 0003 / ADR 0013: nothing here locks, blocks or commands a vehicle.
 */
import type { ChecklistAnswer, ChecklistItemDef, ChecklistResult } from "./checklist.js";
import type { PowerType, VehicleType } from "./domain.js";

/* ---------------- form identity ---------------- */

/** Controlled-document identity of a KYS form, as printed in its page header. */
export type KysFormRef = {
  /** e.g. "KYS-FR-12": 2–24 characters, upper-case letters, digits and dashes. */
  code: string;
  /** e.g. "00": 1–8 characters. */
  revision: string;
  /** Revision date, YYYY-MM-DD (null = not given). */
  revisionDate: string | null;
  /** Form title as printed (TR source, EN translation; EN may be empty). */
  titleTr: string;
  titleEn: string;
  /**
   * Stable key of the form (e.g. "kys-fr12"), kept by copies even when a tenant re-codes its copy. Reports
   * filter on it, never on the code. Absent/null = a form without a known family.
   */
  family?: string | null;
};

/** The FR-12 form family (report_kys_fr12 filters on it). */
export const KYS_FR12_FAMILY = "kys-fr12";

export const KYS_FORM_CODE = /^[A-Z0-9][A-Z0-9-]{1,23}$/;
export const KYS_FORM_REVISION = /^[0-9A-Za-z.]{1,8}$/;

/** Same check as set_checklist_template_form() and the table constraint. */
export function validKysFormRef(f: Pick<KysFormRef, "code" | "revision" | "revisionDate" | "titleTr" | "titleEn">): boolean {
  return (
    KYS_FORM_CODE.test(f.code) &&
    KYS_FORM_REVISION.test(f.revision) &&
    (f.revisionDate === null || /^\d{4}-\d{2}-\d{2}$/.test(f.revisionDate)) &&
    [...f.titleTr.trim()].length >= 1 &&
    [...f.titleTr.trim()].length <= 120 &&
    [...f.titleEn.trim()].length <= 120
  );
}

/** "KYS-FR-12 Rev.00" — how every output names the form. */
export const kysFormLabel = (f: Pick<KysFormRef, "code" | "revision">): string => `${f.code} Rev.${f.revision}`;

/** "KYS-FR-12-000005" — the running document number of one filled-in form (paper: "Belge No"). */
export function kysDocumentNo(code: string, n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isInteger(n) || n < 1) return null;
  return `${code}-${String(n).padStart(6, "0")}`;
}

/* ---------------- FR-12: daily forklift check ---------------- */

export const KYS_FR12_FORM: Readonly<KysFormRef> = Object.freeze({
  code: "KYS-FR-12",
  revision: "00",
  revisionDate: "2025-10-01",
  titleTr: "FORKLİFT GÜNLÜK GÜVENLİK VE ÇALIŞMA KONTROL FORMU",
  titleEn: "DAILY FORKLIFT SAFETY AND OPERATION CHECK FORM",
  family: KYS_FR12_FAMILY,
});

const fr = (code: string, textTr: string, textEn: string, critical: boolean, photoRequiredOnFail: boolean, allowNa = false): ChecklistItemDef => ({
  code,
  textTr,
  textEn,
  critical,
  photoRequiredOnFail,
  powerTypes: [],
  impactFirst: false,
  allowNa,
});

/**
 * The library template "KYS FR-12 Günlük Forklift Kontrol": the 17 items of the paper form, in its order and
 * wording. The paper has no "critical" column; the flags below are the tech lead's proposal (brakes, forks,
 * chains, tyres, horn, connections, belt and the start-up test are critical) and a tenant can change them on
 * its copy. Photo on "Uygun değil" for damage items, like the standard templates. "Yok" (N/A) is allowed on
 * every non-critical item (e.g. engine oil on an electric truck) and on the seat belt (some trucks have none).
 * The migration seeds exactly these items (a database test checks parity).
 */
export const KYS_FR12_TEMPLATE: Readonly<{ nameTr: string; nameEn: string; form: KysFormRef; items: readonly ChecklistItemDef[] }> = Object.freeze({
  nameTr: "KYS FR-12 Günlük Forklift Kontrol",
  nameEn: "KYS FR-12 Daily Forklift Check",
  form: KYS_FR12_FORM,
  items: Object.freeze([
    fr("1", "Lastiklerin durumu", "Condition of the tyres", true, true),
    fr("2", "Çatalların sağlamlığı", "Forks sound and undamaged", true, true),
    fr("3", "Zincir ve kaldırma sistemi", "Chains and lifting system", true, true),
    fr("4", "Frenler", "Brakes", true, false),
    fr("5", "El freni / Park freni", "Hand brake / parking brake", true, false),
    fr("6", "Korna", "Horn", true, false),
    fr("7", "Geri vites ikaz sesi", "Reversing alarm", false, false),
    fr("8", "Görüş aynaları", "Mirrors", false, true),
    fr("9", "Hidrolik yağ seviyesi", "Hydraulic oil level", false, false),
    fr("10", "Motor yağı / şanzıman yağı", "Engine oil / transmission oil", false, false),
    fr("11", "LPG tüp bağlantısı / Akü bağlantısı", "LPG cylinder connection / battery connection", true, true),
    fr("12", "Güç kabloları – hortumlar", "Power cables and hoses", true, true),
    fr("13", "Emniyet kemeri", "Seat belt", true, false, true),
    fr("14", "Koltuk ve oturma pozisyonu", "Seat and seating position", false, false),
    fr("15", "Yangın tüpü durumu", "Fire extinguisher condition", false, false),
    fr("16", "Genel temizlik", "General cleanliness", false, false),
    fr("17", "Çalıştırma testi", "Start-up and function test", true, false),
  ]),
});

/** One filled-in FR-12 form as the report returns it (report_kys_fr12). No operator name (KVKK). */
export type KysFr12Form = {
  submissionId: string;
  vehicleId: string;
  fleetNo: string;
  vehicleType: VehicleType;
  make: string | null;
  model: string | null;
  siteName: string | null;
  form: KysFormRef;
  documentNo: number | null;
  submittedAt: string;
  shiftName: string | null;
  /** The operator was identified from the card session (the name is never in the form output). */
  operatorKnown: boolean;
  result: ChecklistResult;
  approvedAt: string | null;
  /** Role of the approver at approval time: a tenant role or "platform_admin". Never a name. */
  approvedRole: KysApproverRole | null;
  answers: { position: number; code: string; textTr: string; textEn: string; critical: boolean; answer: ChecklistAnswer; note: string | null }[];
};

export const KYS_APPROVER_ROLES = ["fleet_manager", "admin", "platform_admin"] as const;
export type KysApproverRole = (typeof KYS_APPROVER_ROLES)[number];

/** One row of the FR-12 item table: the paper's "Kontrol Maddesi | Uygun | Uygun Değil | Açıklama". */
export type KysFr12Line = { code: string; text: string; ok: boolean; notOk: boolean; na: boolean; note: string };

/**
 * Answers → the rows of the paper table, in form order. "Yok" (N/A) has no column on the paper: neither box is
 * ticked and the remark says it (`naText`, e.g. "Yok (uygulanmaz)") before any note.
 */
export function kysFr12Lines(form: Pick<KysFr12Form, "answers">, lang: "tr" | "en", naText: string): KysFr12Line[] {
  return [...form.answers]
    .sort((a, b) => a.position - b.position)
    .map((a) => {
      const text = lang === "en" && a.textEn.trim() ? a.textEn : a.textTr;
      const note = a.note?.trim() ?? "";
      return {
        code: a.code,
        text,
        ok: a.answer === "ok",
        notOk: a.answer === "not_ok",
        na: a.answer === "na",
        note: a.answer === "na" ? [naText, note].filter(Boolean).join(" — ") : note,
      };
    });
}

/* ---------------- FR-11: equipment inventory and maintenance ---------------- */

export const KYS_FR11_FORM: Readonly<KysFormRef> = Object.freeze({
  code: "KYS-FR-11",
  revision: "00",
  revisionDate: "2025-12-01",
  titleTr: "EKİPMAN ENVANTER VE BAKIM TAKİBİ FORMU",
  titleEn: "EQUIPMENT INVENTORY AND MAINTENANCE TRACKING FORM",
});

/**
 * Who is responsible for a vehicle's upkeep: a ROLE, never a person (KVKK; the paper form lists names). The
 * list follows the equipment maintenance procedure's responsibilities plus an outside maintenance firm.
 */
export const MAINTENANCE_ROLES = ["operations_chief", "workshop_chief", "technician", "fleet_manager", "safety_officer", "order_management", "external_service"] as const;
export type MaintenanceRole = (typeof MAINTENANCE_ROLES)[number];

/** periodic = planned maintenance; annual_inspection = statutory yearly periodic inspection; repair = breakdown repair. */
export const MAINTENANCE_KINDS = ["periodic", "annual_inspection", "repair"] as const;
export type MaintenanceKind = (typeof MAINTENANCE_KINDS)[number];

export const MAINTENANCE_LIMITS = Object.freeze({
  intervalMinMonths: 1,
  intervalMaxMonths: 60,
  noteMax: 200,
  /** A correction must say why the earlier record is void. */
  voidReasonMax: 200,
  /** "Due soon" this many days before the due date. */
  dueSoonDays: 30,
  /** The statutory periodic inspection is yearly. */
  inspectionMonths: 12,
  /** A record is never dated after today in the vehicle's site time zone (Europe/Istanbul by default). */
  futureDays: 0,
});

export type MaintenancePlan = { vehicleId: string; tenantId: string; intervalMonths: number | null; responsibleRole: MaintenanceRole | null; updatedAt: string };
/**
 * Records are never deleted or edited. A correction is a new record that voids an earlier one
 * (`voidsRecordId` + `voidReason`); `voided` = a later record voids this one (it no longer counts).
 */
export type MaintenanceRecord = {
  id: string;
  tenantId: string;
  vehicleId: string;
  kind: MaintenanceKind;
  doneOn: string;
  note: string | null;
  createdAt: string;
  voidsRecordId: string | null;
  voidReason: string | null;
  voided: boolean;
};

/** One row of the FR-11 report (report_kys_fr11). */
export type KysFr11Row = {
  vehicleId: string;
  fleetNo: string;
  vehicleType: VehicleType;
  power: PowerType;
  make: string | null;
  model: string | null;
  serialNo: string | null;
  siteId: string;
  siteName: string | null;
  active: boolean;
  intervalMonths: number | null;
  responsibleRole: MaintenanceRole | null;
  lastPeriodicOn: string | null;
  lastInspectionOn: string | null;
  lastRepairOn: string | null;
  openFaults: number;
  openCritical: number;
};

/** Same rules as the guard trigger of vehicle_maintenance_plans. */
export function validMaintenancePlan(p: { intervalMonths: number | null; responsibleRole: string | null }): boolean {
  const okInterval = p.intervalMonths === null || (Number.isInteger(p.intervalMonths) && p.intervalMonths >= MAINTENANCE_LIMITS.intervalMinMonths && p.intervalMonths <= MAINTENANCE_LIMITS.intervalMaxMonths);
  const okRole = p.responsibleRole === null || (MAINTENANCE_ROLES as readonly string[]).includes(p.responsibleRole);
  return okInterval && okRole;
}

/** Same rules as the guard trigger of vehicle_maintenance_records (`today` = the local date YYYY-MM-DD). */
export function validMaintenanceRecord(r: { kind: string; doneOn: string; note: string | null; voidsRecordId?: string | null; voidReason?: string | null }, today: string): boolean {
  const reason = r.voidReason?.trim() ?? "";
  if ((r.voidsRecordId ? 1 : 0) !== (reason ? 1 : 0) || [...reason].length > MAINTENANCE_LIMITS.voidReasonMax) return false;
  if (!(MAINTENANCE_KINDS as readonly string[]).includes(r.kind)) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.doneOn) || r.doneOn < "2000-01-01" || Number.isNaN(Date.parse(`${r.doneOn}T00:00:00Z`))) return false;
  if (r.doneOn > addDaysIso(today, MAINTENANCE_LIMITS.futureDays)) return false;
  return r.note === null || [...r.note.trim()].length <= MAINTENANCE_LIMITS.noteMax;
}

function addDaysIso(day: string, n: number): string {
  return new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Calendar months later (31 Jan + 1 month = 28/29 Feb, like Postgres date + interval). */
export function addMonthsIso(day: string, months: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1 + months;
  const ty = y + Math.floor(mo / 12);
  const tm = ((mo % 12) + 12) % 12;
  const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  const d = Math.min(Number(m[3]), last);
  return `${ty}-${String(tm + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Status of a due date: overdue (before today), due_soon (within dueSoonDays), ok, or unknown (no date yet:
 * never maintained / inspected in FTS, or no interval).
 */
export type DueStatus = "overdue" | "due_soon" | "ok" | "unknown";
export function dueStatus(due: string | null, today: string): DueStatus {
  if (!due) return "unknown";
  if (due < today) return "overdue";
  return due <= addDaysIso(today, MAINTENANCE_LIMITS.dueSoonDays) ? "due_soon" : "ok";
}

/** Overall status shown in the FR-11 "Durum" column, most urgent first. */
/**
 * `no_inspection`: an active vehicle whose yearly periodic inspection was never recorded — a WARNING (the
 * inspection is a legal obligation), not the neutral "unknown".
 */
export type Fr11Status = "inactive" | "maintenance_required" | "overdue" | "no_inspection" | "due_soon" | "ok" | "unknown";

export type Fr11Derived = {
  nextPeriodicOn: string | null;
  nextInspectionOn: string | null;
  periodic: DueStatus;
  inspection: DueStatus;
  status: Fr11Status;
};

/**
 * Next due dates and the overall status of an FR-11 row. An open CRITICAL checklist fault shows
 * "maintenance required" (ADR 0013) — information only, the vehicle is never locked.
 */
export function fr11Derived(r: Pick<KysFr11Row, "active" | "intervalMonths" | "lastPeriodicOn" | "lastInspectionOn" | "openCritical">, today: string): Fr11Derived {
  const nextPeriodicOn = r.lastPeriodicOn && r.intervalMonths ? addMonthsIso(r.lastPeriodicOn, r.intervalMonths) : null;
  const nextInspectionOn = r.lastInspectionOn ? addMonthsIso(r.lastInspectionOn, MAINTENANCE_LIMITS.inspectionMonths) : null;
  const periodic = dueStatus(nextPeriodicOn, today);
  const inspection = dueStatus(nextInspectionOn, today);
  const status: Fr11Status = !r.active
    ? "inactive"
    : r.openCritical > 0
      ? "maintenance_required"
      : periodic === "overdue" || inspection === "overdue"
        ? "overdue"
        : !r.lastInspectionOn
          ? "no_inspection"
          : periodic === "due_soon" || inspection === "due_soon"
            ? "due_soon"
            : periodic === "ok" && inspection === "ok"
              ? "ok"
              : "unknown";
  return { nextPeriodicOn, nextInspectionOn, periodic, inspection, status };
}
